import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'gocompareai_search_history';
const MAX_HISTORY = 10;

/**
 * Custom hook for managing AI search history in localStorage
 * @returns {Object} { history, addSearch, removeSearch, clearHistory }
 */
export function useSearchHistory() {
  const [history, setHistory] = useState([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setHistory(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading search history:', error);
    }
  }, []);

  // Save history to localStorage whenever it changes
  const saveHistory = useCallback((newHistory) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      setHistory(newHistory);
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  }, []);

  // Add a new search to history
  const addSearch = useCallback((query) => {
    if (!query || query.trim().length < 5) return;
    
    const trimmedQuery = query.trim();
    const timestamp = new Date().toISOString();
    
    setHistory((prev) => {
      // Remove duplicate if exists (case-insensitive)
      const filtered = prev.filter(
        (item) => item.query.toLowerCase() !== trimmedQuery.toLowerCase()
      );
      
      // Add new search at the beginning
      const newHistory = [
        { query: trimmedQuery, timestamp },
        ...filtered
      ].slice(0, MAX_HISTORY); // Keep only last MAX_HISTORY items
      
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (error) {
        console.error('Error saving search history:', error);
      }
      
      return newHistory;
    });
  }, []);

  // Remove a specific search from history
  const removeSearch = useCallback((query) => {
    setHistory((prev) => {
      const newHistory = prev.filter(
        (item) => item.query.toLowerCase() !== query.toLowerCase()
      );
      
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (error) {
        console.error('Error saving search history:', error);
      }
      
      return newHistory;
    });
  }, []);

  // Clear all history
  const clearHistory = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHistory([]);
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }, []);

  return {
    history,
    addSearch,
    removeSearch,
    clearHistory
  };
}
